import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useQuery, useQueryClient } from '@tanstack/vue-query'

import { SupportChatWidgetService } from '../services/support-chat-widget.service'

import { isUsableChatWidgetId } from '../utils/support-chat-widget-id.utils'

export const supportChatWidgetQueryKeys = {
  all: () => ['supportChatWidget'] as const,
  embed: (locationId: string, widgetId: string) => [...supportChatWidgetQueryKeys.all(), 'embed', locationId, widgetId] as const,
}

export const useSupportChatWidgetQueryStore = () => {
  const queryClient = useQueryClient()
  const service = new SupportChatWidgetService()

  const clearSupportChatWidgetCache = () => {
    queryClient.removeQueries({ queryKey: supportChatWidgetQueryKeys.all() })
  }

  const removeSupportChatEmbedCache = (locationId: string, widgetId: string) => {
    queryClient.removeQueries({ queryKey: supportChatWidgetQueryKeys.embed(locationId, widgetId) })
  }

  /**
   * Embed HTML for a location + widget. TanStack Query owns server state (long stale; removed on logout / invalidation).
   * Callers pass `locationId` and `widgetId` (e.g. from `useLocationStore`) so this layer stays independent of location-layer internals.
   */
  const fetchSupportChatEmbedQuery = (
    locationId: MaybeRefOrGetter<string>,
    widgetId: MaybeRefOrGetter<string>,
    enabled: MaybeRefOrGetter<boolean>
  ) => {
    const loc = () => toValue(locationId) ?? ''
    const wid = () => toValue(widgetId)

    return useQuery({
      queryKey: computed(() => supportChatWidgetQueryKeys.embed(loc(), wid())),
      queryFn: async (): Promise<string | null> => {
        const lid = loc()
        const id = wid()
        if (!lid || !isUsableChatWidgetId(id)) return null
        const payload = await service.fetchEmbedCode(lid, id)
        const trimmed = payload?.code?.trim()
        return trimmed || null
      },
      enabled: computed(() => !!loc() && isUsableChatWidgetId(wid()) && !!toValue(enabled)),
      staleTime: Infinity,
      gcTime: 30 * 60 * 1000,
      retry: false,
    })
  }

  return { fetchSupportChatEmbedQuery, clearSupportChatWidgetCache, removeSupportChatEmbedCache }
}
